<template>
  <div class="data-view-wrapper clearfix" v-if="gatewayInfo && !loading">
    <b-alert v-if="accountStatus" show :variant="accountStatus.variant" class="w-100">
      <strong>{{ $t(accountStatus.labelKey, accountStatus.labelDefault) }}</strong>
      <div>{{ $t(accountStatus.messageKey, accountStatus.messageDefault) }}</div>
    </b-alert>

    <crowded-payout-account
      v-if="isGateway('crowded') && !crowdedBankApplicationStatusDenied"
      class="w-100"
      :gateway-id="gateway.id"
      :approved="crowdedBankApplicationApproved"
      :show-crowded-note="false"
      :balance="balanceStatus === 'ready' ? balance : null"
    />

    <!-- Card readers: the venue they belong to, and the key the org's event
         devices present when they ask for a connection token. -->
    <stripe-terminal-panel
      v-if="isTerminalGateway"
      class="w-100"
      :gateway-id="gatewayInfo.id"
      :campaign-ids="gatewayCampaignIds"
    />

    <!-- Crowded bank application status sits with payouts, which depend on it -->
    <div v-if="isGateway('crowded') && crowdedBankApplicationStatus" class="w-100 tw-mb-4">
      <div class="data-view">
        <div class="data-view__title">
          {{ $t("gateways.bank_application_status", "Bank application status") }}
        </div>
        <div class="data-view__data">
          <b-badge pill :variant="crowdedBankApplicationVariant">
            {{ crowdedBankApplicationStatus }}
          </b-badge>
        </div>
      </div>
      <b-alert v-if="!crowdedBankApplicationApproved" show variant="warning" class="w-100 tw-mb-0">
        <div>
          {{
            crowdedBankApplicationStatusDenied
              ? $t(
                  "gateways.crowded_application_denied_info",
                  "Your bank application was denied, so payouts can't be made."
                )
              : $t(
                  "gateways.crowded_application_pending_info",
                  "Payouts will only occur once the application is approved."
                )
          }}
        </div>
        <div>
          {{ $t("gateways.crowded_support_visit", "For support, visit") }}
          <a :href="crowdedSupportUrl" target="_blank" rel="noopener noreferrer">{{
            crowdedSupportUrlLabel
          }}</a>
          {{ $t("gateways.crowded_support_or_email", "or email") }}
          <a :href="`mailto:${crowdedSupportEmail}`">{{ crowdedSupportEmail }}</a>
        </div>
      </b-alert>
    </div>

    <div class="data-view">
      <div class="data-view__title">
        {{ $t("gateways.gateway_name") }}
      </div>
      <div class="data-view__data">
        {{ getAltNames(gatewayInfo.attributes.gateway) || gatewayInfo.attributes.gateway }}
      </div>
    </div>

    <div class="data-view" v-if="entityName">
      <div class="data-view__title">
        {{ $t("gateways.legal_entity") }}
      </div>
      <div class="data-view__data">
        {{ entityName.attributes.name }}
      </div>
    </div>

    <div class="data-view">
      <div class="data-view__title">
        {{ $t("gateways.status") }}
      </div>
      <div class="data-view__data">
        {{ gatewayInfo.attributes.active ? $t("gateways.enabled") : $t("gateways.disabled") }}
      </div>
    </div>

    <div class="data-view">
      <div class="data-view__title">
        {{ $t("gateways.primary_currency") }}
      </div>
      <div class="data-view__data">
        {{ gatewayInfo.attributes.primary_currency }}
      </div>
    </div>

    <div class="data-view" v-if="balanceStatus && !isGateway('crowded')">
      <div class="data-view__title">
        {{ $t("gateways.balance", "Balance") }}
      </div>
      <div class="data-view__data">
        <b-skeleton v-if="balanceStatus === 'pending'" width="160px" height="16px" />
        <template v-else-if="balanceStatus === 'ready' && balance">
          <div v-if="availableBalanceText">
            {{ $t("gateways.balance_available", "Available") }}: {{ availableBalanceText }}
          </div>
          <div v-if="pendingBalanceText">
            {{ $t("gateways.balance_pending", "Pending") }}: {{ pendingBalanceText }}
          </div>
          <div v-if="capturedBalanceText">
            {{ $t("gateways.balance_captured", "Captured") }}: {{ capturedBalanceText }}
          </div>
          <div v-if="inTransitBalanceText">
            {{ $t("gateways.balance_in_transit", "In transit") }}: {{ inTransitBalanceText }}
          </div>
        </template>
        <span v-else-if="balanceStatus === 'error'" class="text-muted">
          {{ $t("gateways.balance_unavailable", "Unavailable") }}
        </span>
      </div>
    </div>

    <div class="data-view" v-if="payoutSchedule">
      <div class="data-view__title">
        {{ $t("gateways.payout_schedule", "Payout schedule") }}
      </div>
      <div class="data-view__data">
        {{ payoutScheduleText }}
      </div>
    </div>

    <div
      class="data-view"
      v-for="(value, key) in orderedItems(gatewayInfo.attributes.gateway_data)"
      :key="key"
    >
      <div class="data-view__title">
        {{ $t("gateways." + key, snakeToSentenceCase(key)) }}
        <span v-if="key === 'statement_display'">
          <tooltip icon="info">
            <div class="help-tooltip__message">{{ $t("gateways.info_message") }}</div>
          </tooltip>
        </span>
      </div>

      <div v-if="isValueHTML(key)" class="data-view__data" v-html="value"></div>

      <div v-else-if="isUrlButton(key) && value" class="data-view__data">
        <b-button size="sm" class="bttn bttn--sm bttn--blue" @click="openUrlModal(key, value)" pill>
          {{ urlButtonLabel(key) }}
        </b-button>
      </div>

      <div v-else-if="!(edit === key)" class="data-view__data">
        {{ key == "verification_due_by" ? setTime(value) : value }}
      </div>

      <div v-if="showTestMode(key)" class="switch-public--switch">
        <input
          :id="`switch_gateways_turn_off_on_${key}`"
          v-model="testMode"
          type="checkbox"
          @input="testModeOffOn"
        />
        <label :for="`switch_gateways_turn_off_on_${key}`"></label>
      </div>

      <div
        v-if="!(edit === key) && isEditable(key) && isEditableForGateway(key)"
        class="data-view__action"
      >
        <button class="bttn bttn-round" @click="setEdit(key, value)">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
            ></path>
            <path d="M0 0h24v24H0z" fill="none"></path>
          </svg>
        </button>
      </div>

      <div v-if="edit === key" class="edit-field">
        <div class="form-unit data-view__data">
          <div v-if="key === 'disable_card_swiper'">
            <b-form-checkbox :id="key" :name="key" v-model="editForm[key]" v-validate="'required'">
            </b-form-checkbox>
          </div>
          <div v-if="key === 'api_sign'">
            <b-form-checkbox
              :id="key"
              :name="key"
              value="true"
              unchecked-value="false"
              v-model="editForm[key]"
            >
              {{ $t("gateways.yaadpay_api_sign") }}
            </b-form-checkbox>
          </div>
          <div v-if="key === 'statement_display'" class="form-unit__addon-wrap">
            <div class="form-unit__addon-icon form-unit__addon-icon--wide">CHARIDY</div>
            <input
              v-model="editForm[key]"
              type="text"
              :name="key"
              class="form-control form-unit__input"
              v-validate="'required|max:14'"
            />
          </div>
          <div v-if="key === 'custom_label'" class="form-unit__addon-wrap">
            <input
              v-model="editForm[key]"
              type="text"
              :name="key"
              class="form-control form-unit__input"
              v-validate="'required|max:255'"
            />
          </div>
          <div v-if="key === 'message'" class="form-unit__addon-wrap">
            <textarea
              v-model="editForm[key]"
              type="text"
              :name="key"
              class="form-control form-unit__textarea"
              v-validate="'required|max:255'"
              rows="3"
            />
          </div>
          <div v-if="key === 'charge_currency_list'" class="form-group">
            <multiselect
              :options="gateway.attributes.available_charge_currency_list"
              :multiple="true"
              :value="editForm[key]"
              :key="chargeCurrencyListKey"
              @input="onChargeCurrencyListChange"
            />
          </div>
          <span v-show="vErrors.has(key)" class="v-help is-danger">
            {{ $t("gateways.statement_length_error") }}
          </span>
        </div>
        <div class="d-flex flex-column align-items-center justify-content-center">
          <div class="data-view__action my-1">
            <input
              type="submit"
              class="bttn bttn--orange bttn--sm"
              :value="$t('account.save')"
              @click="save"
            />
          </div>
          <div class="data-view__action my-1">
            <input
              type="submit"
              class="bttn bttn--blue bttn--sm"
              :value="$t('account.confirm_cancel')"
              @click="setEdit('')"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Secondary on purpose: payouts above are the primary way to move funds -->
    <div v-if="isGateway('crowded')" class="crowded-account-box w-100">
      <div class="crowded-account-box__text">
        <b>{{ $t("gateways.crowded_account_title", "Your Crowded account") }}</b>
        <div>
          {{
            $t(
              "gateways.crowded_account_help",
              "View transactions, cards and statements, or cancel a pending payout, directly on Crowded. Sign in with the phone number associated with your account."
            )
          }}
        </div>
        <div>
          {{ $t("gateways.crowded_support_visit", "For support, visit") }}
          <a :href="crowdedSupportUrl" target="_blank" rel="noopener noreferrer">{{
            crowdedSupportUrlLabel
          }}</a>
          {{ $t("gateways.crowded_support_or_email", "or email") }}
          <a :href="`mailto:${crowdedSupportEmail}`">{{ crowdedSupportEmail }}</a>
        </div>
      </div>
      <span v-b-tooltip.hover :title="crowdedAccessButtonTooltip" class="d-inline-block">
        <b-button
          :href="crowdedAccessLink"
          target="_blank"
          rel="noopener noreferrer"
          variant="outline-secondary"
          size="sm"
          :disabled="crowdedBankApplicationStatusDenied"
        >
          {{ $t("gateways.crowded_open_dashboard", "Open Crowded dashboard") }} ↗
        </b-button>
      </span>
    </div>

    <GatewayInvoiceSettings
      :gateway-id="gateway.id"
      :gateway-info="gatewayInfo"
      @saved="loadGatewaysData"
    />

    <div v-if="required" class="my-4 text-center">
      {{ $t("gateways.stripe_acc_additional_verification") }}
      <a :href="link">Click here</a>
    </div>

    <button class="bttn bttn--lg bttn--blue" @click.prevent="updateStep">
      {{ $t("gateways.close_view") }}
    </button>

    <AdditionalConsentUrlModal ref="urlModal" />
  </div>
  <div v-else class="text-center">
    <LdsSpinner />
  </div>
</template>

<script>
import axios from "axios";
import urls from "@/constants/urls.js";
import moment from "moment-timezone";
import findIndex from "lodash/findIndex";
import { mapState } from "vuex";
import tooltip from "@/components/tooltip";
import { getAltNames } from "@/constants/gateway-alt-names";
import { formatBalanceAmounts } from "@/helpers/format-money.js";
import { formatPayoutSchedule } from "@/helpers/format-payout-schedule.js";
import { ACCOUNT_STATUS_KEY, getAccountStatus } from "@/constants/gateway-account-status";

import Multiselect from "vue-multiselect";
import { notificationsMixin } from "@/mixins";
import LdsSpinner from "@/common-components/ui-elements/lds-spinner.vue";
import AdditionalConsentUrlModal from "@/components/additional-consent-url-modal.vue";
import GatewayInvoiceSettings from "./gatewayInvoiceSettings.vue";
import CrowdedPayoutAccount from "./crowdedPayoutAccount.vue";
import StripeTerminalPanel from "./stripeTerminalPanel.vue";
import {
  CROWDED_APP_URL,
  CROWDED_SUPPORT_EMAIL,
  CROWDED_SUPPORT_URL,
} from "@/constants/crowded.js";

export default {
  props: ["gateway"],

  data: function () {
    return {
      gatewayInfo: null,
      edit: "",
      editForm: {},
      errors: [],
      required: false,
      link: "",
      testMode: false,
      tokenizeOnly: false,
      chargeCurrencyListKey: "",
      loading: false,
      crowdedAccessLink: CROWDED_APP_URL,
      crowdedSupportEmail: CROWDED_SUPPORT_EMAIL,
      crowdedSupportUrl: CROWDED_SUPPORT_URL,
      crowdedSupportUrlLabel: CROWDED_SUPPORT_URL.replace(/^https:\/\//, "").replace(/\/$/, ""),
      balancePollInterval: null,
    };
  },

  beforeDestroy() {
    if (this.balancePollInterval) {
      clearInterval(this.balancePollInterval);
    }
  },

  mixins: [notificationsMixin],

  components: {
    tooltip,
    Multiselect,
    LdsSpinner,
    AdditionalConsentUrlModal,
    GatewayInvoiceSettings,
    CrowdedPayoutAccount,
    StripeTerminalPanel,
  },

  computed: {
    ...mapState({
      entityName: function (state) {
        let entitiArr = state.account.entities;

        let gatewayEntityId = this.gatewayInfo.attributes.entity_id;

        let entity = entitiArr.filter((el) => {
          return el.id == gatewayEntityId;
        });

        if (entity.length > 0) {
          return entity[0];
        }

        return false;
      },

      timeZone: (state) => {
        return state.account.account.data.attributes.timezone;
      },
    }),

    // Only these two take payments through a card reader, so only they have a
    // venue and a device key. Kept in step with the backend, which refuses the
    // terminal routes for any other gateway.
    isTerminalGateway() {
      return ["stripe-terminal", "stripe-tap-to-pay"].includes(
        this.gatewayInfo?.attributes?.gateway
      );
    },

    // The campaigns this payment option serves, used to build a device link.
    gatewayCampaignIds() {
      return (this.gateway?.relationships?.cids?.data || [])
        .map((item) => item?.attributes?.cid)
        .filter(Boolean);
    },

    // set only when the provider details could not be fetched for this gateway
    accountStatus() {
      return getAccountStatus(this.gatewayInfo?.attributes);
    },

    crowdedBankApplicationStatus() {
      if (this.gatewayInfo && this.gatewayInfo.attributes.gateway === "crowded") {
        return this.gatewayInfo.attributes.gateway_data.bank_application_status;
      }
      return null;
    },

    crowdedBankApplicationStatusDenied() {
      return this.crowdedBankApplicationStatus?.toLowerCase() === "denied";
    },

    crowdedBankApplicationApproved() {
      return this.crowdedBankApplicationStatus?.toLowerCase() === "approved";
    },

    crowdedAccessButtonTooltip() {
      if (this.crowdedBankApplicationStatusDenied) {
        return this.$t(
          "gateways.crowded_access_funds_denied_tooltip",
          "Your application has been denied. Please contact support for more information."
        );
      }

      return this.$t(
        "gateways.crowded_access_funds_tooltip",
        "Access your Crowded banking dashboard to manage your funds and view transactions."
      );
    },

    crowdedBankApplicationVariant() {
      if (this.crowdedBankApplicationApproved) return "success";
      return this.crowdedBankApplicationStatusDenied ? "danger" : "warning";
    },

    balanceStatus() {
      return this.gatewayInfo?.attributes?.balance_status || null;
    },

    balance() {
      return this.gatewayInfo?.attributes?.balance || null;
    },

    payoutSchedule() {
      return this.gatewayInfo?.attributes?.payout_schedule || null;
    },

    availableBalanceText() {
      return formatBalanceAmounts(this.balance?.available, { skipZero: true });
    },

    pendingBalanceText() {
      return formatBalanceAmounts(this.balance?.pending, { skipZero: true });
    },

    capturedBalanceText() {
      return formatBalanceAmounts(this.balance?.captured, { skipZero: true });
    },

    inTransitBalanceText() {
      return formatBalanceAmounts(this.balance?.in_transit, { skipZero: true });
    },

    payoutScheduleText() {
      return formatPayoutSchedule(this.payoutSchedule, this.$t.bind(this));
    },
  },

  methods: {
    getAltNames,

    snakeToSentenceCase(str) {
      return str
        .split("_")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
    },

    isGateway(gateway) {
      return this.gatewayInfo.attributes.gateway === gateway;
    },

    isUrlButton(key) {
      return ["additional_consent_url", "form_url"].includes(key);
    },

    urlButtonLabel(key) {
      if (key === "additional_consent_url") {
        return this.$t(
          "gateways.crowded_additional_consent_url_button",
          "Provide Additional Consent"
        );
      }
      return this.$t("gateways.crowded_open_form_button", "Open Application Form");
    },

    openUrlModal(key, url) {
      this.$refs.urlModal.open(url, this.urlButtonLabel(key));
    },

    showTestMode(field) {
      if (field === "test_mode") {
        switch (this.gatewayInfo.attributes.gateway) {
          case "stripe":
          case "stripe-element":
          case "stripe-apple-pay":
          case "stripe-google-pay":
            return true;
        }
      }
      return false;
    },

    orderedItems(items) {
      items.created_at = moment(this.gateway.attributes.created_at * 1000).format(
        "YYYY-MM-DD HH:mm"
      );

      // the receipt service keys are rendered by the invoice settings section, and
      // account_status is not a provider detail — it says why the others are absent,
      // so it is shown as an alert above instead of a raw key/value row.
      const visibleItems = {};
      for (const key in items) {
        if (key.indexOf("receipt_service") === 0 || key === ACCOUNT_STATUS_KEY) {
          continue;
        }
        // Crowded's application status and payout account are rendered in the payouts panel.
        if (
          this.isGateway("crowded") &&
          (key === "bank_application_status" || key.indexOf("payout_ach") === 0)
        ) {
          continue;
        }
        visibleItems[key] = items[key];
      }
      items = visibleItems;

      if (this.gatewayInfo.attributes.gateway == "stripe") {
        const array = [];
        for (let key in items) {
          array.push({ [key]: items[key] });
        }
        const indexPrevItem = findIndex(array, (i) =>
          Object.prototype.hasOwnProperty.call(i, "bank_account_last_4")
        );
        const indexNextItem = findIndex(array, (i) =>
          Object.prototype.hasOwnProperty.call(i, "routing_number")
        );
        // both are absent when the provider details could not be fetched — the
        // duplicate-row shuffle below only makes sense when both exist.
        if (indexPrevItem === -1 || indexNextItem === -1) {
          return items;
        }
        array.splice(indexPrevItem + 1, 0, array[indexNextItem]);
        const sortItems = {};
        array.map((i) => {
          sortItems[Object.keys(i)[0]] = i[Object.keys(i)[0]];
        });
        return sortItems;
      } else {
        return items;
      }
    },

    updateStep() {
      this.$emit("update-step");
    },

    setTime(date) {
      const d = date * 1000;

      if (d <= 0) {
        return this.$t("gateways.verification_due_by_not_required", "Not required");
      }

      return moment.tz(d, this.timeZone).format("YYYY-MM-DD HH:mm");
    },

    loadGatewaysData: function () {
      this.loading = true;
      let orgId = localStorage.getItem("orgId");
      axios
        .get(urls.getGatewayData.replace(":orgId", orgId).replace(":gatewayId", this.gateway.id))

        .then(({ data }) => {
          this.gatewayInfo = data.data;

          this.testMode = this.gatewayInfo.attributes.gateway_data?.test_mode === "true";

          if (this.gatewayInfo.attributes.gateway == "stripe") {
            this.getStripeOnBoarding(this.gatewayInfo.id).then(({ data }) => {
              const { required, link } = data.data.attributes;

              this.required = required;
              this.link = link;
            });
          }

          this.maybePollBalance();
        })

        .catch((e) => {
          if (e.response.data) {
            if (e.response.data.error) {
              this.errors.push(e.response.data.error);
            } else {
              this.errors.push(this.$t("template.error_default"));
            }
          } else {
            this.errors.push(this.$t("template.error_default"));
          }
        })

        .finally(() => {
          this.loading = false;
        });
    },

    getStripeOnBoarding(id) {
      let orgId = localStorage.getItem("orgId");

      return axios.get(urls.stripeOnBoarding.replace(":oid", orgId).replace(":idGateway", id));
    },

    // Balance/payout-schedule data is fetched asynchronously on the backend
    // (cached for 1h); while balance_status is "pending", poll silently
    // (no full-view loading spinner) until it resolves.
    refreshBalance() {
      let orgId = localStorage.getItem("orgId");
      axios
        .get(urls.getGatewayData.replace(":orgId", orgId).replace(":gatewayId", this.gateway.id))
        .then(({ data }) => {
          this.gatewayInfo = data.data;
          this.maybePollBalance();
        })
        .catch(() => {});
    },

    maybePollBalance() {
      const isPending = this.balanceStatus === "pending";
      if (isPending && !this.balancePollInterval) {
        this.balancePollInterval = setInterval(this.refreshBalance, 8000);
      } else if (!isPending && this.balancePollInterval) {
        clearInterval(this.balancePollInterval);
        this.balancePollInterval = null;
      }
    },

    isEditable(key) {
      return [
        "statement_display",
        "charge_currency_list",
        "custom_label",
        "message",
        "disable_card_swiper",
        "api_sign",
      ].includes(key);
    },

    isEditableForGateway(key) {
      const keysForGateways = {
        charge_currency_list: [
          "stripe",
          "stripe-element",
          "stripe-ideal",
          "stripe_becs",
          "stripe_apple_google_pay",
        ],
        api_sign: ["yaadpay"],
      };

      if (!keysForGateways[key]) {
        return true;
      }

      return keysForGateways[key].includes(this.gatewayInfo.attributes.gateway);
    },

    isValueHTML(key) {
      return (
        ["lead_comment"].includes(key) && this.gatewayInfo.attributes.gateway.includes("meshulam")
      );
    },

    setEdit(field, value = "") {
      this.edit = field;

      if (field) {
        switch (field) {
          case "statement_display": {
            const sep = ["*", " "];
            for (let i = 0; i < sep.length; i++) {
              const s = sep[i];
              if (value.split(s)[1]) {
                this.editForm[field] = value.split(s)[1];
                break;
              }
            }
            break;
          }
          case "charge_currency_list":
            this.editForm[field] = value.split(", ");
            this.chargeCurrencyListKey = value + Math.random();
            break;
          case "custom_label":
          case "message":
            this.editForm[field] = value;
            break;

          default:
            this.editForm[field] = value;
            break;
        }
      } else {
        this.editForm = {};
      }
    },

    onChargeCurrencyListChange(value) {
      this.$set(this.editForm, "charge_currency_list", value);
      this.chargeCurrencyListKey = value.join("") + Math.random();
    },

    save() {
      let orgId = localStorage.getItem("orgId");

      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.editForm.statement_display) {
            axios
              .post(
                urls.stripeDescriptorUpdate
                  .replace(":oid", orgId)
                  .replace(":idGateway", this.gateway.id),
                {
                  statement: "Charidy " + this.editForm.statement_display,
                }
              )
              .then(() => {
                this.loadGatewaysData();
              })
              .catch(this.$_notificationsMixin_handleCatch)
              .finally(() => {
                this.setEdit("");
              });
          } else {
            axios
              .patch(
                urls.getGatewayData.replace(":orgId", orgId).replace(":gatewayId", this.gateway.id),
                { ...this.editForm }
              )
              .then(() => {
                this.loadGatewaysData();
              })
              .catch(this.$_notificationsMixin_handleCatch)
              .finally(() => {
                this.setEdit("");
              });
          }
        }
      });
    },

    testModeOffOn() {
      this.editForm["test_mode"] = !this.testMode;
      this.save();
    },

    clearDataModa: function () {
      this.gatewayInfo = null;
      this.errors = [];
    },
  },

  created() {
    this.loadGatewaysData();
  },
};
</script>

<style lang="scss">
.data-view {
  align-items: center;
}
</style>

<style lang="scss" scoped>
.data-view {
  margin-bottom: 20px;
  border-bottom: 1px solid lightgrey;
  display: flex;
  flex-wrap: wrap;
  @media only screen and (min-width: 992px) {
    // flex-wrap: nowrap;
  }
  &__title {
    min-width: 100%;
    padding: 10px 0;
    font-family: $vito-bold;
    font-size: 16px;
    @media only screen and (min-width: 992px) {
      min-width: 30%;
      max-width: 30%;
      margin-right: 20px;
    }
  }
  &__data {
    flex-grow: 1;
    padding: 10px 0;
    font-family: $vito-light;
    font-size: 16px;
    word-wrap: break-word;
    max-width: 65%;
  }
}

@media (max-width: 768px) {
  .data-view__data {
    max-width: 100%;
  }
}
.crowded-account-box {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 16px 0;
  padding: 12px 16px;
  border: 1px solid #e6eaed;
  border-radius: 6px;
  background-color: #f8f9fa;
  font-size: 14px;
  color: #6f7a8a;

  &__text {
    flex: 1 1 280px;

    b {
      color: $fd;
    }
  }
}

.data-view-wrapper {
  button {
    float: right;
  }
}

.data-view__action {
  display: flex;
  align-items: center;
  padding-left: 10px;
  cursor: pointer;
}

.edit-field {
  display: contents;
}
</style>
